// Create a provider that will be used to pass the event data to the pages.

import { is } from "make-plural";
import React from "react";
import { z } from "zod";
import { FragmentType, useFragment as getFragment, graphql } from "~/gql";

const varsSchema = z
  .array(
    z.object({
      key: z.string(),
      value: z.string(),
    })
  )
  // Transform to object
  .transform((vars) =>
    vars.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {})
  );

const siteConfigSchema = z.object({
  tastingNotesEnabled: z
    .string()
    .optional()
    .default("true")
    .transform((v) => v === "true"),
  listsEnabled: z
    .string()
    .optional()
    .default("true")
    .transform((v) => v === "true"),
  showDistributionDetails: z
    .enum(["inMarket", "global", "all", "none"])
    .optional()
    .default("inMarket"),
  isChatEnabled: z
    .string()
    .optional()
    .transform((v) => v === "true"),
  isMeetingBookingEnabled: z
    .string()
    .optional()
    .transform((v) => v === "true"),
});

const CollectionLayoutContext = React.createContext<{
  collectionId: string;
  logo?: {
    publicId?: string;
    asset?: {
      path?: string;
    };
  };
  isSanity: boolean;
  projectId?: string;
  siteConfig: z.infer<typeof siteConfigSchema>;
}>({
  collectionId: "",
  logo: undefined,
  isSanity: false,
  projectId: undefined,
  siteConfig: siteConfigSchema.parse({}),
});

const fragment = graphql(/* GraphQL */ `
  fragment CollectionLayoutProvider on Collection {
    collectionId
    outputConfigurations: _unstable_allOutputConfigurations(
      experimentalKey: "jonathan@bottlebooks.me: @bottlebooks/bottlebooks-site-rr/src/pages/[locale]/collections/[collectionId]/_layout.tsx"
    ) {
      outputType
      vars {
        key
        value
      }
    }
    collectionId
    logo {
      publicId
    }
  }
`);

export default function CollectionLayoutProvider({
  children,
  data,
  collectionId,
}: {
  children: React.ReactNode;
  data: FragmentType<typeof fragment>;
  collectionId: string;
}) {
  const collection = getFragment(fragment, data);
  const value = collection
    ? {
        collectionId,
        logo: collection.logo,
        isSanity: collection.isSanity,
        projectId: collection.projectId,

        siteConfig: resolveSiteConfig(data),
      }
    : {
        collectionId,
        logo: null,
        isSanity: false,
        projectId: null,
        siteConfig: siteConfigSchema.parse({}),
      };
  return (
    <CollectionLayoutContext.Provider value={value}>
      {children}
    </CollectionLayoutContext.Provider>
  );
}

function resolveSiteConfig(data: FragmentType<typeof fragment>) {
  const collection = getFragment(fragment, data);
  const websiteConfiguration = collection.outputConfigurations.find(
    ({ outputType }) => outputType === "WEBSITE"
  );
  if (!websiteConfiguration) throw new Error("No website configuration found.");
  const { vars } = websiteConfiguration;
  const varsAsObject = varsSchema.parse(vars);
  return siteConfigSchema.parse(varsAsObject);
}

export function useCollectionLayout() {
  const data = React.useContext(CollectionLayoutContext);
  return data;
}
